// LeadEdit.tsx
import React, { useState, useEffect, useRef } from "react";
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import { LeadData } from "../../pages/Clients/Admin/Function/LeadFunctions";
import { useSubAgentAuth } from '../../pages/Clients/SubAgent/SubAgentAuthContext';
import { useAuth } from "../../AuthContext";
// Define FollowUp type here if not available elsewhere
export interface FollowUp {
    id?: string;
    followUp: string;
    followUpTime: Timestamp;
    leadId: string;
    userId: string;
    assignedUserName?: string;
    isDone: boolean;
}

interface Source {
    id: string;
    source: string;
}

interface SubSource {
    id: string;
    subSource: string;
    sourceId: string;
}

interface Campaign {
    id: string;
    campaign: string;
}

interface Pipeline {
    id: string;
    pipelineName: any;
}

interface UserOption {
    id: string;
    name: string;
    lastName: string;
    email: string;
    distribution?: number;
}


interface LeadEditProps {
    isOpen: boolean;
    currentUser: any;
    isStaff: boolean;
    currentScreen: string;
    leadId: string;
    onClose: () => void;
    onSuccess?: (message: string) => void;
}

const LeadEdit: React.FC<LeadEditProps> = ({
    isOpen,
    currentUser,
    isStaff,
    currentScreen,
    leadId,
    onClose,
    onSuccess,
    //   onUpdate,
}) => {

    const { userData } = useAuth();

    const { currentSubAgent } = useSubAgentAuth();
    const [newLead, setNewLead] = useState<Partial<LeadData>>({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    // Inside the StudentDetails component, add these states:
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState<LeadData | null>(null);
    const [sources, setSources] = useState<Source[]>([]);
    const [subSources, setSubSources] = useState<SubSource[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [pipelines, setPipelines] = useState<Pipeline[]>([]);
    const [assignedToOptions, setAssignedToOptions] = useState<UserOption[]>([]);
    const [currentUserPipelines, setCurrentUserPipelines] = useState<string[]>([]);

    // Alternate phone state variables
    const [isAlternatePhoneRequired, setIsAlternatePhoneRequired] = useState(false);
    const [showSecondAlternatePhone, setShowSecondAlternatePhone] = useState(false);

    // Phone validation state variables
    const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("");
    const [isPhoneValidating, setIsPhoneValidating] = useState<boolean>(false);
    const [phoneValidationType, setPhoneValidationType] = useState<'error' | 'warning' | 'success' | ''>("");

    // Update validation state variables
    const [updateValidationMessage, setUpdateValidationMessage] = useState<string>("");
    const [updateValidationType, setUpdateValidationType] = useState<'error' | 'warning' | 'success' | ''>("");


    const fetchLeadData = async (companyId: string) => {
        try {
            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const leadData = leadDoc.data() as LeadData;

                // Find if the lead has any existing follow-up
                let followUpText = "";
                let followUpTime = Timestamp.fromDate(new Date());
                const followUpRef = leadData?.activeFollowUpId ? doc(db, `companies/${companyId}/followUps`, leadData.activeFollowUpId) : null;

                if (followUpRef) {
                    const followUpDoc = await getDoc(followUpRef);
                    if (followUpDoc.exists()) {
                        const followUpData = followUpDoc.data() as FollowUp;
                        followUpText = followUpData.followUp || "";
                        followUpTime = followUpData.followUpTime || Timestamp.fromDate(new Date());
                    }
                }

                setNewLead({
                    ...leadData,
                    followUpText,
                    followUpTime,
                });

                // Initialize alternate phone state based on existing data
                const hasAlternateNumber1 = leadData.alternateNumber1 && leadData.alternateNumber1.trim() !== "";
                const hasAlternateNumber2 = leadData.alternateNumber2 && leadData.alternateNumber2.trim() !== "";

                if (hasAlternateNumber1 || hasAlternateNumber2) {
                    setIsAlternatePhoneRequired(true);
                    if (hasAlternateNumber2) {
                        setShowSecondAlternatePhone(true);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching lead data:", error);
        }
    };

    const fetchResources = async (companyId: string) => {
        try {
            console.log("Fetching resources for companyId:", companyId, "and leadId:", leadId);
            // Fetch sources
            const sourcesQuery = query(collection(db, `companies/${companyId}/leadSources`));
            const sourcesSnapshot = await getDocs(sourcesQuery);
            console.log("Sources fetched:", sourcesSnapshot);

            const sourcesData = sourcesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Source[];
            setSources(sourcesData);

            // Fetch subSources
            const subSourcesQuery = query(collection(db, `companies/${companyId}/leadSubSources`));
            const subSourcesSnapshot = await getDocs(subSourcesQuery);
            const subSourcesData = subSourcesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as SubSource[];
            setSubSources(subSourcesData);

            // Fetch campaigns
            const campaignsQuery = query(collection(db, `companies/${companyId}/leadCampaigns`));
            const campaignsSnapshot = await getDocs(campaignsQuery);
            const campaignsData = campaignsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Campaign[];
            setCampaigns(campaignsData);

            // Fetch pipelines
            const pipelinesQuery = query(collection(db, `companies/${companyId}/leadPipelines`));
            const pipelinesSnapshot = await getDocs(pipelinesQuery);
            const pipelinesData = pipelinesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Pipeline[];
            setPipelines(pipelinesData);

            console.log("Pipelines fetched:", pipelinesData);



            // Fetch users (for assignedTo options)
            const usersQuery = query(collection(db, `users`), where("companyId", "==", companyId));
            const usersSnapshot = await getDocs(usersQuery);
            const usersData = usersSnapshot.docs
                .filter(doc => doc.data().isActive === true) // Only include explicitly active users
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as UserOption[];

            // Sort users alphabetically by name (case-insensitive)
            const sortedUsersData = usersData.sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });

            setAssignedToOptions(sortedUsersData);
            console.log("Assigned to options fetched:", sortedUsersData);


            // Fetch current user pipelines (you may need to adjust this based on your auth structure)
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
                setCurrentUserPipelines(userDoc.data().pipelineIds || []);
            }

        } catch (error) {
            console.error("Error fetching resources:", error);
        }
    }


    // Check if number already exists in the database (excluding current lead)
    const checkNumberExists = async (number: string, companyId: string): Promise<{
        exists: boolean;
        leadData: any | null;
        assigneeName?: string;
    }> => {
        if (!companyId || !number) return { exists: false, leadData: null };

        try {
            const leadsRef = collection(db, `companies/${companyId}/leads`);
            const q = query(leadsRef, where("number", "==", number));
            const querySnapshot = await getDocs(q);

            // Exclude the current lead being edited
            const duplicateDocs = querySnapshot.docs.filter(doc => doc.id !== leadId);
            if (duplicateDocs.length > 0) {
                const docData = duplicateDocs[0].data();
                const leadData = { id: duplicateDocs[0].id, ...docData } as any;

                // Fetch assignee name
                let assigneeName = "Unknown";
                if (leadData.assignedTo) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            assigneeName = userData.name || "Unknown";
                        }
                    } catch (error) {
                        console.error("Error fetching assignee name:", error);
                    }
                }

                return {
                    exists: true,
                    leadData: leadData,
                    assigneeName: assigneeName
                };
            }

            return { exists: false, leadData: null };
        } catch (error) {
            console.error("Error checking number existence:", error);
            return { exists: false, leadData: null };
        }
    };

    // Real-time phone number validation function
    const validatePhoneRealTime = async (phoneNumber: string, companyId: string) => {
        setPhoneValidationMessage("");
        setPhoneValidationType("");

        if (!phoneNumber || phoneNumber.length < 1) {
            return;
        }

        setIsPhoneValidating(true);

        try {
            let formattedNumber = phoneNumber.replace(/\D/g, '');
            
            // Add + prefix if not already present and number has digits
            if (!phoneNumber.startsWith('+') && formattedNumber.length > 0) {
                formattedNumber = "+" + formattedNumber;
            } else if (phoneNumber.startsWith('+')) {
                formattedNumber = phoneNumber;
            } else {
                formattedNumber = "+" + formattedNumber;
            }

            const result = await checkNumberExists(formattedNumber, companyId);

            if (result.exists && result.leadData) {
                const existingLeadName = result.leadData.name || "Unknown";
                const assigneeName = result.assigneeName || "Unknown";
                setPhoneValidationMessage(`Lead already exists: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setPhoneValidationType("error");
            } else {
                setPhoneValidationMessage("Phone number is available");
                setPhoneValidationType("success");
            }
        } catch (error) {
            console.error("Error validating phone number:", error);
            setPhoneValidationMessage("Error validating phone number");
            setPhoneValidationType("error");
        } finally {
            setIsPhoneValidating(false);
        }
    };


    useEffect(() => {
        const fetchData = async () => {
            console.log("LeadEdit useEffect triggered with leadId:", leadId);
            // Clear any previous validation messages when modal opens
            setUpdateValidationMessage("");
            setUpdateValidationType("");
            
            const currentUser = auth.currentUser;

            if (!currentUser || !leadId) {
                console.error("Missing required data:", { currentUser, leadId, companyId: currentSubAgent?.companyId });
                return;
            }

            let companyId = '';

            if (currentUser?.uid) {
                try {
                    const userData = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userData.exists()) {
                        companyId = userData.data().companyId || '';
                        console.log("Company ID fetched:", companyId);
                    } else {
                        console.error("User data not found for UID:", currentUser.uid);
                        return;
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                    return;
                }
            } else if (currentSubAgent?.companyId) {
                companyId = currentSubAgent.companyId;
            }

            fetchLeadData(companyId);
            fetchResources(companyId);
        };

        fetchData();
    }, [leadId]);

    // Real-time phone number validation with debouncing
    useEffect(() => {
        if (!newLead.number || newLead.number.length < 1) {
            setPhoneValidationMessage("");
            setPhoneValidationType("");
            return;
        }

        const timeoutId = setTimeout(async () => {
            // Get current companyId
            let companyId = '';
            if (currentSubAgent?.companyId) {
                companyId = currentSubAgent.companyId;
            } else if (auth.currentUser?.uid) {
                try {
                    const userData = await getDoc(doc(db, 'users', auth.currentUser.uid));
                    if (userData.exists()) {
                        companyId = userData.data().companyId || '';
                    }
                } catch (error) {
                    console.error("Error fetching user data for validation:", error);
                    return;
                }
            }

            if (companyId) {
                validatePhoneRealTime(newLead.number || "", companyId);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [newLead.number, leadId, currentSubAgent?.companyId]);




    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const upperCased = input.value.toLocaleUpperCase();

        setNewLead((prev) => ({ ...prev, name: upperCased }));

        // Restore cursor after render
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.setSelectionRange(start, end);
            }
        });
    };

    const handlePipelineSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedPipeline = e.target.value;

        setNewLead(prev => {
            const currentPipelineIds = prev.pipelines || [];

            if (currentPipelineIds.includes(selectedPipeline)) {
                return prev;
            }

            if (currentPipelineIds.length >= 5) {
                return prev;
            }

            return {
                ...prev,
                pipelines: [...currentPipelineIds, selectedPipeline]
            };
        });
    }

    const handleRemovePipeline = (pipelineId: string) => {
        setNewLead(prev => ({
            ...prev,
            pipelines: (prev.pipelines ?? []).filter(id => id !== pipelineId)
        }));
    }

    const handleUpdateLead = async () => {
        console.log("Starting handleUpdateLead function");

        setSubmitLoading(true);

        const currentUser = auth.currentUser;
        if (!currentUser || !leadId) {
            console.error("Missing required data:", { currentUser, leadId });
            setSubmitLoading(false);
            return;
        }

        // Get companyId - prioritize currentSubAgent, fallback to user document
        let companyId = '';
        if (currentSubAgent?.companyId) {
            companyId = currentSubAgent.companyId;
        } else if (currentUser?.uid) {
            try {
                const userData = await getDoc(doc(db, 'users', currentUser.uid));
                if (userData.exists()) {
                    companyId = userData.data().companyId || '';
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                setSubmitLoading(false);
                return;
            }
        }

        if (!companyId) {
            console.error("No company ID found");
            setSubmitLoading(false);
            return;
        }

        // Validation
        if (!newLead.name || !newLead.number || !newLead.assignedTo) {
            alert("Please fill in all required fields (Name, Phone, and Assigned To)");
            setSubmitLoading(false);
            return;
        }

        // Phone number validation
        const cleanedNumber = newLead.number.replace(/\D/g, '');
        if (cleanedNumber.length < 1) {
            alert("Phone number is required");
            setSubmitLoading(false);
            return;
        }

        try {
            // Clear any previous update validation messages
            setUpdateValidationMessage("");
            setUpdateValidationType("");

            // Format phone number with +
            const formattedNumber = "+" + cleanedNumber;

            // Check for duplicate phone number before updating
            const numberCheckResult = await checkNumberExists(formattedNumber, companyId);
            if (numberCheckResult.exists && numberCheckResult.leadData) {
                const existingLeadName = numberCheckResult.leadData.name || "Unknown";
                const assigneeName = numberCheckResult.assigneeName || "Unknown";
                
                setSubmitLoading(false);
                setUpdateValidationMessage(`Lead Already Exists with this Number. Existing Lead: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setUpdateValidationType("error");
                return;
            }

            // Clean alternate numbers - only save if they contain actual numbers beyond +91
            const cleanAlternateNumber1 = newLead.alternateNumber1?.trim();
            const cleanAlternateNumber2 = newLead.alternateNumber2?.trim();
            
            const alternateNumber1 = (cleanAlternateNumber1 && cleanAlternateNumber1 !== "+91" && cleanAlternateNumber1.length > 3) 
                ? cleanAlternateNumber1 : "";
            const alternateNumber2 = (cleanAlternateNumber2 && cleanAlternateNumber2 !== "+91" && cleanAlternateNumber2.length > 3) 
                ? cleanAlternateNumber2 : "";

            const updatedLead = {
                id: leadId,
                name: newLead.name?.trim(),
                number: formattedNumber,
                alternateNumber1: alternateNumber1,
                alternateNumber2: alternateNumber2,
                reversedNumber: formattedNumber.split('').reverse().join(''),
                reversedAlternateNumber1: alternateNumber1 ? alternateNumber1.split('').reverse().join('') : "",
                reversedAlternateNumber2: alternateNumber2 ? alternateNumber2.split('').reverse().join('') : "",
                email: newLead.email || "",
                assignedTo: newLead.assignedTo || "",
                adminNotes: newLead.adminNotes || "",
                sourceId: newLead.sourceId || "",
                subSourceId: subSources.find(s => s.id === newLead.subSourceId && s.sourceId === newLead.sourceId)?.id || null,
                stageId: newLead.stageId || "",
                stateId: newLead.stateId || "",
                statusId: newLead.statusId || "",
                campaignId: newLead.campaignId || "",
                admissionStatusId: newLead.admissionStatusId || "",
                pipelines: newLead.pipelines || [],
                updatedBy: currentUser.uid,
                updatedAt: Timestamp.fromDate(new Date()),
                createdAt: newLead.createdAt ? newLead.createdAt : Timestamp.fromDate(new Date()),
                isActive: true,
                transferredAt: newLead.transferredAt ?? null
            };

            // Update lead document
            await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), updatedLead);
            console.log("Lead updated successfully");

            // Handle follow-up if provided
            if (newLead?.followUpText?.trim()) {
                // Use current user's name as assignedUserName
                const assignedUserName = userData?.name || 'Unknown';

                const followUpData = {
                    followUp: newLead.followUpText.trim(),
                    followUpTime: newLead.followUpTime || Timestamp.fromDate(new Date()),
                    leadId: leadId,
                    userId: newLead.assignedTo,
                    assignedUserName: assignedUserName,
                    isDone: false,
                    isEdited: false,
                    createdAt: Timestamp.fromDate(new Date()),
                    createdBy: currentUser.uid,
                    originalAssigneeId: newLead.assignedTo,
                };

                if (newLead.activeFollowUpId) {
                    // Mark the existing follow-up as done and edited
                    await updateDoc(
                        doc(db, `companies/${companyId}/followUps`, newLead.activeFollowUpId),
                        {
                            isDone: true,
                            isEdited: true,
                            updatedAt: Timestamp.fromDate(new Date()),
                            updatedBy: currentUser.uid
                        }
                    );

                    // Create a new follow-up with the updated details
                    const newFollowUpRef = await addDoc(
                        collection(db, `companies/${companyId}/followUps`),
                        followUpData
                    );

                    // Update the new follow-up document with its ID
                    await updateDoc(newFollowUpRef, { id: newFollowUpRef.id });

                    // Update lead with new follow-up reference
                    await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), {
                        hasActiveFollowUp: true,
                        activeFollowUpId: newFollowUpRef.id,
                        activeFollowUpTime: followUpData.followUpTime,
                        activeFollowUpText: followUpData.followUp || null,
                    });
                    console.log("Existing follow-up marked as done/edited and new follow-up created");
                } else {
                    // Create new follow-up (no existing follow-up to mark as done)
                    const followUpRef = await addDoc(
                        collection(db, `companies/${companyId}/followUps`),
                        followUpData
                    );

                    // Update lead with follow-up reference
                    await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), {
                        hasActiveFollowUp: true,
                        activeFollowUpId: followUpRef.id,
                        activeFollowUpTime: followUpData.followUpTime,
                        activeFollowUpText: followUpData.followUp || null,
                    });

                    // Add ID to follow-up document
                    await updateDoc(followUpRef, { id: followUpRef.id });
                    console.log("New follow-up created");
                }
            } else if (newLead.activeFollowUpId) {
                // Remove follow-up if text was cleared
                await updateDoc(doc(db, `companies/${companyId}/leads`, leadId), {
                    hasActiveFollowUp: false,
                    activeFollowUpId: null,
                    activeFollowUpTime: null,
                    activeFollowUpText: null,
                });
                console.log("Follow-up removed");
            }

            console.log("Lead update completed successfully");
            onClose(); // Close the modal

            // Reload the page after a short delay to ensure all updates are processed
            setTimeout(() => {
                window.location.reload();
            }, 300);

        } catch (error) {
            console.error("Error updating lead:", error);
            alert("An error occurred while updating the lead. Please try again.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const convertToDateString = (timestamp: Timestamp | Date): string => {
        if (timestamp instanceof Timestamp) {
            const date = timestamp.toDate();
            date.setMinutes(date.getMinutes() + 330); // Add 5 hours and 30 minutes (330 minutes)
            return date.toISOString().slice(0, 16); // Returns in 'YYYY-MM-DDTHH:mm' format
        } else {
            throw new Error("Invalid timestamp type");
        }
    }

    const handlePhoneBlur = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        const fieldValue = newLead[field];
        // Only set +91 for the main phone number field, not for alternate numbers
        if (field === "number" && (!fieldValue || fieldValue.trim() === "")) {
            setNewLead({ ...newLead, [field]: "+91" });
        }
        // For alternate numbers, clear the field if it's empty or only contains +91
        if ((field === "alternateNumber1" || field === "alternateNumber2") && 
            (!fieldValue || fieldValue.trim() === "" || fieldValue.trim() === "+91")) {
            setNewLead({ ...newLead, [field]: "" });
        }
    };
    
    const handlePhoneFocus = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        if (field === "number") {
            // Clear validation state when focusing on main phone number
            setPhoneValidationMessage('');
            setPhoneValidationType('');
        }

        const fieldValue = newLead[field];
        if (!fieldValue || fieldValue.trim() === "") {
            setNewLead({ ...newLead, [field]: "+91" });
        }
    };

    // Helper function to filter out non-numeric characters (except + for country code)
    const handlePhoneChange = (field: "number" | "alternateNumber1" | "alternateNumber2", value: string) => {
        // Allow only numbers and + symbol (for country code), remove letters and spaces
        const numericValue = value.replace(/[^0-9+]/g, '');
        setNewLead({ ...newLead, [field]: numericValue });
        
        // Clear update validation message when phone number changes
        if (field === "number") {
            setUpdateValidationMessage("");
            setUpdateValidationType("");
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            {isOpen && (
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden">
                    <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                        <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Staff Icon" />
                                </div>
                                <h2 className="text-xl font-semibold text-black">Edit Lead</h2>
                            </div>
                            <div className="text-right">
                                <button onClick={onClose} className="font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 mt-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Name"
                                    value={newLead.name || ""}
                                    onChange={handleChange}
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-6 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{`Assigned To ${currentScreen === 'Staff' ? '(myself)' : ''}`} <span className="text-red-500">*</span></label>
                                    <select
                                        value={newLead.assignedTo || ""}
                                        onChange={(e) => {
                                            const selectedUserId = e.target.value;
                                            setNewLead({ ...newLead, assignedTo: selectedUserId });
                                        }}
                                        className={`border p-2 w-full mb-2 ${isStaff && currentScreen === 'Staff' ? 'bg-gray-200 appearance-none cursor-not-allowed font-semibold' : ''}`}
                                        disabled={isStaff && currentScreen === 'Staff'}
                                        style={{
                                            borderRadius: '24px',
                                            border: '1px solid #3483BE',
                                        }}
                                    >
                                        {currentScreen !== 'Staff' && (<option value="">Assign To</option>)}
                                        {assignedToOptions.length > 0 ? (
                                            assignedToOptions.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option disabled>No users available</option>
                                        )}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Phone Number"
                                        value={newLead.number || ""}
                                        onFocus={() => handlePhoneFocus("number")}
                                        onBlur={() => handlePhoneBlur("number")}
                                        onChange={(e) =>
                                            handlePhoneChange("number", e.target.value)
                                        }
                                        className={`border p-2 w-full mb-2 ${phoneValidationType === 'error' ? 'border-red-500' :
                                            phoneValidationType === 'success' ? 'border-green-500' :
                                                phoneValidationType === 'warning' ? 'border-yellow-500' :
                                                    'border-[#3483BE]'
                                            }`}
                                        style={{ borderRadius: '24px' }}
                                    />
                                    {(phoneValidationMessage || isPhoneValidating) && (
                                        <div className={`text-xs mt-1 ${phoneValidationType === 'error' ? 'text-red-500' :
                                            phoneValidationType === 'success' ? 'text-green-500' :
                                                phoneValidationType === 'warning' ? 'text-yellow-500' :
                                                    'text-gray-500'
                                            }`}>
                                            {isPhoneValidating ? 'Validating...' : phoneValidationMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Alternate Phone Numbers */}
                        <div className="mt-4">
                            {!isAlternatePhoneRequired ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Alternate Phone Number Required?
                                    </label>
                                    <div className="flex gap-4 mb-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="alternatePhoneRequired"
                                                value="yes"
                                                checked={false}
                                                onChange={() => setIsAlternatePhoneRequired(true)}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="alternatePhoneRequired"
                                                value="no"
                                                checked={true}
                                                onChange={() => {
                                                    setIsAlternatePhoneRequired(false);
                                                    setShowSecondAlternatePhone(false);
                                                    setNewLead({ ...newLead, alternateNumber1: "", alternateNumber2: "" });
                                                }}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Alternate Numbers
                                    </label>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="flex flex-col space-y-4">
                                            <input
                                                type="text"
                                                placeholder="Alternate Phone Number"
                                                value={newLead.alternateNumber1 || ""}
                                                onFocus={() => handlePhoneFocus("alternateNumber1")}
                                                onBlur={() => handlePhoneBlur("alternateNumber1")}
                                                onChange={(e) =>
                                                    handlePhoneChange("alternateNumber1", e.target.value)
                                                }
                                                className="border p-3 w-full"
                                                style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                            />
                                            {showSecondAlternatePhone && (
                                                <input
                                                    type="text"
                                                    placeholder="Second Alternate Number"
                                                    value={newLead.alternateNumber2 || ""}
                                                    onFocus={() => handlePhoneFocus("alternateNumber2")}
                                                    onBlur={() => handlePhoneBlur("alternateNumber2")}
                                                    onChange={(e) =>
                                                        handlePhoneChange("alternateNumber2", e.target.value)
                                                    }
                                                    className="border p-3 w-full"
                                                    style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                                />
                                            )}
                                        </div>

                                        <div className="flex items-start">
                                            {!showSecondAlternatePhone && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSecondAlternatePhone(true)}
                                                    className="bg-blue-500 text-white px-4 py-3 rounded-full text-sm hover:bg-blue-600 w-full max-w-20"
                                                >
                                                    +
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <div className="flex flex-col space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    value={newLead.email || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, email: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Source <span className="text-red-500">*</span></label>
                                <select
                                    value={newLead.sourceId || ""}
                                    onChange={(e) => {
                                        const selectedSourceId = e.target.value;
                                        setNewLead({ ...newLead, sourceId: selectedSourceId });
                                    }}
                                    className={`border p-2 w-full mb-2 ${isStaff && newLead.sourceId && currentScreen === 'Staff' ? 'bg-gray-200 appearance-none cursor-not-allowed font-semibold' : ''}`}
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                    disabled={currentScreen === 'Staff' && isStaff && !!newLead.sourceId}
                                >
                                    <option value="">Select Source</option>
                                    {sources.length > 0 ? (
                                        sources.map((source) => (
                                            <option key={source.id} value={source.id}>
                                                {source.source}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No sources available</option>
                                    )}
                                </select>
                            </div>

                            {newLead.sourceId && subSources.length > 0 && subSources.some(subSource => subSource.sourceId === newLead.sourceId) && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Sub Source</label>
                                    <select
                                        value={newLead.subSourceId || ""}
                                        onChange={(e) => {
                                            const selectedSubSourceId = e.target.value;
                                            setNewLead({ ...newLead, subSourceId: selectedSubSourceId });
                                        }}
                                        className="border p-2 w-full mb-2"
                                        style={{
                                            borderRadius: '24px',
                                            border: '1px solid #3483BE',
                                        }}
                                    >
                                        <option value="">Select Sub Source</option>
                                        {subSources.filter(subSource => subSource.sourceId === newLead.sourceId).map((subSource) => (
                                            <option key={subSource.id} value={subSource.id}>
                                                {subSource.subSource}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Campaign</label>
                                <select
                                    value={newLead.campaignId || ""}
                                    onChange={(e) => {
                                        const selectedCampaignId = e.target.value;
                                        setNewLead({ ...newLead, campaignId: selectedCampaignId });
                                    }}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                >
                                    <option value="">Select Campaign</option>
                                    {campaigns.length > 0 ? (
                                        campaigns.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>
                                                {campaign.campaign}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No campaign available</option>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Pipeline</label>
                                <select
                                    onChange={handlePipelineSelection}
                                    value={newLead.pipelines || ""}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                >
                                    <option value="">Select Pipeline</option>
                                    {pipelines.length > 0 ? (
                                        pipelines
                                            .filter(pipeline => currentUserPipelines?.includes(pipeline.id))
                                            .map((pipeline) => (
                                                <option key={pipeline.id} value={pipeline.id}>
                                                    {pipeline.pipelineName}
                                                </option>
                                            ))
                                    ) : (
                                        <option disabled>No Pipeline available</option>
                                    )}
                                </select>

                                {(newLead.pipelines?.length ?? 0) > 0 && (
                                    <div className="mt-2">
                                        <div className="flex flex-wrap gap-2">
                                            {newLead.pipelines?.map((pipelineId) => {
                                                const pipeline = pipelines.find(p => p.id === pipelineId);
                                                return (
                                                    <div
                                                        key={pipelineId}
                                                        className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                                                    >
                                                        {pipeline ? pipeline.pipelineName : pipelineId}
                                                        {pipeline && currentUserPipelines.includes(pipeline.id) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemovePipeline(pipelineId)}
                                                                className="text-red-500 cursor-pointer"
                                                            >
                                                                <img
                                                                    src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                                    alt="Remove"
                                                                    className="w-4 h-4"
                                                                />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-8">
                            <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
                            <div className="relative">
                                <textarea
                                    placeholder="Admin Notes"
                                    value={newLead.adminNotes || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, adminNotes: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-4">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                        alt="Delete Icon"
                                        className="w-5 h-5 cursor-pointer"
                                        onClick={() => setNewLead({ ...newLead, adminNotes: "" })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-8">
                            <label className="block text-sm font-medium text-gray-700">Follow Ups</label>
                            <div className="relative">
                                <textarea
                                    placeholder="Add Follow Ups"
                                    value={newLead.followUpText || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, followUpText: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-4">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                        alt="Delete Icon"
                                        className="w-5 h-5 cursor-pointer"
                                        onClick={() => setNewLead({ ...newLead, followUpText: "" })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Follow Up Time</label>
                                <input
                                    type="datetime-local"
                                    value={newLead.followUpTime ? convertToDateString(newLead.followUpTime) : ""}
                                    placeholder="Follow Up Time"
                                    min={new Date().toISOString().slice(0, 16)}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                    onChange={(e) => setNewLead({ ...newLead, followUpTime: Timestamp.fromDate(new Date(e.target.value)) })}
                                />
                            </div>
                        </div>

                        {/* Update Validation Message */}
                        {updateValidationMessage && (
                            <div className={`mt-4 p-3 rounded-lg text-sm ${
                                updateValidationType === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                                updateValidationType === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                updateValidationType === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                                'bg-gray-50 text-gray-700 border border-gray-200'
                            }`}>
                                {updateValidationMessage}
                            </div>
                        )}

                        <div className="mt-8 flex justify-end">
                            {submitLoading ? (
                                <button className="px-8 py-2 bg-gradient-to-b from-[#FFA782] to-[#FC583D] text-white rounded-3xl flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Submitting...
                                </button>
                            ) : (
                                <button
                                    onClick={handleUpdateLead}
                                    className="px-8 py-2 bg-gradient-to-b from-[#FFA782] to-[#FC583D] text-white rounded-3xl"
                                >
                                    Submit
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadEdit;